import { defineComponent as _defineComponent } from 'vue'
import { computed } from 'vue';

import { ObjectType } from '@/@types/graphql-types';
import type { GlobalTop10TitleFragment } from '@/pages/graphql/fragments/GlobalTop10Title.fragment';

import PosterGraphql from '@/components/poster/PosterGraphql.vue';
import TitleDetailLink from '@/components/nav/links/TitleDetailLink.vue';
import WatchNowButtonNew from '@/components/buttons/WatchNowButtonNew.vue';
import BasicButton from '@/ui-components/BasicButton.vue';
import PictureComp from '@/components/picture/PictureComp.vue';

import { faBookmark, faCheck } from '@fortawesome/free-solid-svg-icons';
import { formatRuntime } from '@/helpers/title-helper';
import { getEffectiveJwRating } from '@/helpers/scoring-helper';
import { useI18n } from '@/helpers/composables/useI18n';
import { SnowplowContext, SnowplowOldModuleContext } from '@/helpers/tracking/providers';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { useTitleWatchlist } from '@/helpers/composables/useTitleWatchlist';


export default /*#__PURE__*/_defineComponent({
  __name: 'GlobalTop10SwiperSlide',
  props: {
    title: null,
    index: null,
    moduleName: null,
    isSponsored: { type: Boolean, default: false },
    hideJwScore: { type: Boolean, default: false },
    hideImdbScore: { type: Boolean, default: false }
  },
  emits: ["posterClick", "titleClickout", "watchlistClick", "moreInfoClick", "imageError"],
  setup(__props: any, { emit: emits }: { emit: ({
	(e: 'posterClick', data: { title: GlobalTop10TitleFragment; index: number }): void;
	(e: 'titleClickout', data: { title: GlobalTop10TitleFragment; index: number }): void;
	(e: 'watchlistClick', data: { title: GlobalTop10TitleFragment; index: number }): void;
	(e: 'moreInfoClick', data: { title: GlobalTop10TitleFragment; index: number }): void;
	(e: 'imageError', src: string | null | undefined): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { title: GlobalTop10TitleFragment, index: number, moduleName: string, isSponsored: boolean, hideJwScore: boolean, hideImdbScore: boolean };





const { t, tc } = useI18n();

const releaseYear = computed(() => props.title.content.originalReleaseYear);

const runtime = computed(() => {
	if (props.title?.content?.runtime) {
		const runtimeValue = props.title.content.runtime!;
		const { translationKey, minutes, hours } = formatRuntime(runtimeValue);
		return t(translationKey, { minutes, hours }) as string;
	}
	return null;
});

const jwScore = computed(() => {
	if (props.hideJwScore) return null;
	return Math.round((getEffectiveJwRating(props.title.content.scoring) || 0) * 100);
});

const imdbScore = computed(() => {
	if (props.hideImdbScore) return null;
	return props.title.content.scoring?.imdbScore?.toFixed(1);
});

const trackingLabel = computed(() => {
	if (props.title.objectType === ObjectType.Movie) return 'top_10_movies';
	if (props.title.objectType === ObjectType.Show) return 'top_10_shows';
	return '';
});

const moduleContext = computed(() => new SnowplowOldModuleContext('', -1, props.moduleName, ''));

const titleContext = computed(() => {
	return TrackingHelper.getTitleContextGraphql(props.title.objectId, props.title.objectType, undefined);
});

const imageUrls = computed(() => [] as string[]);

const isInWatchlist = computed(() => {
	return !!props.title.watchlistEntryV2?.createdAt;
});

const mutationTitleDetails = computed(() => {
	return {
		objectId: props.title.objectId,
		objectType: props.title.objectType,
		title: props.title.content.title,
	};
});

const { setInWatchlist } = useTitleWatchlist(undefined, [titleContext.value, moduleContext.value]);

function addToWatchList() {
	// The orchestrator owns the mutation, so it can pair it with the WatchlistClicked SRA event.
	if (props.isSponsored) {
		emits('watchlistClick', { title: props.title, index: props.index });
		return;
	}

	setInWatchlist(props.title.id, isInWatchlist.value, mutationTitleDetails.value, {
		source: 'lists-panel',
	});
}

function preventNavigation($event: Event) {
	$event.preventDefault();
	$event.stopPropagation();
	$event.stopImmediatePropagation();
}

function handlePosterClick(title: GlobalTop10TitleFragment, index: number, contexts: SnowplowContext[] = []) {
	emits('posterClick', { title, index });

	if (props.isSponsored) return;

	TrackingHelper.trackEvent(
		'userinteraction',
		{
			action: 'title_clicked',
			label: trackingLabel.value,
			value: index,
		},
		[...contexts, titleContext.value, moduleContext.value]
	);
}

function trackMoreClicked() {
	if (props.isSponsored) {
		emits('moreInfoClick', { title: props.title, index: props.index });
		return;
	}

	TrackingHelper.trackEvent(
		'userinteraction',
		{
			action: 'more_clicked',
			property: 'global_home_swiper',
		},
		[moduleContext.value, titleContext.value]
	);
}

return { __sfc: true,props, emits, t, tc, releaseYear, runtime, jwScore, imdbScore, trackingLabel, moduleContext, titleContext, imageUrls, isInWatchlist, mutationTitleDetails, setInWatchlist, addToWatchList, preventNavigation, handlePosterClick, trackMoreClicked, PosterGraphql, TitleDetailLink, WatchNowButtonNew, BasicButton, PictureComp, faBookmark, faCheck }
}

})